import {
  ComponentProps,
  CSSProperties,
  MouseEvent,
  ReactNode,
  SyntheticEvent,
  useMemo,
} from "react";
import ArticleLabelIcon from "../../assets/icons/article-label.svg?react";
import DotsIcon from "../../assets/icons/dots-circle-black.svg?react";
import HeartBlackIcon from "../../assets/icons/heart-circle-black.svg?react";
import HeartRedIcon from "../../assets/icons/heart-circle-red.svg?react";
import InfographicLabelIcon from "../../assets/icons/infographic-label.svg?react";
import JournalIcon from "../../assets/icons/journal.svg?react";
import JuisciMainAvatar from "../../assets/icons/juisci-main-avatar.svg?react";
import JuisciGPTLabelIcon from "../../assets/icons/juisciGPT-label.svg?react";
import NavRoomsBlackIcon from "../../assets/icons/nav/nav-rooms-black.svg?react";

import PencilIcon from "../../assets/icons/pencil.svg?react";
import PlaylistLabelIcon from "../../assets/icons/playlist-label.svg?react";
import PodcastLabelIcon from "../../assets/icons/podcast-label.svg?react";
import VideoLabelIcon from "../../assets/icons/video-label.svg?react";
import { renderDate } from "../../domains/app/utils/renderDate";
import ClampLines from "../app/ClampLines";
import ResponsiveImage from "../app/ResponsiveImage";
import ProfilePicture from "../ProfilePicture";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";

export function CardContainer({
  children,
  variant,
  blurThumbnail,
  style,
  backgroundUrl,
  backgroundImage,
  onClick,
  colorLayerOverrides,
  contentStyleOverrides,
}: {
  children: ReactNode;
  variant?: "white" | "yellow" | "dark";
  blurThumbnail?: boolean;
  style?: CSSProperties;
  backgroundUrl: string | null;
  backgroundImage: { url: string; path: string } | null;
  onClick?: (e: SyntheticEvent) => void;
  colorLayerOverrides?: CSSProperties;
  contentStyleOverrides?: CSSProperties;
}) {
  return (
    <div
      style={style}
      className={`card-outer-container isolate box-border relative flex-auto! h-full min-h-[280px] rounded-2xl shadow-card border border-black/2 flex overflow-hidden md:w-full ${onClick ? "cursor-pointer" : ""}`}
      onClick={(e: SyntheticEvent) => onClick?.(e)}
    >
      {backgroundImage !== null && (
        <ResponsiveImage
          image={backgroundImage}
          alt=""
          className="rounded-2xl"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "inherit",
            filter: blurThumbnail === true ? "blur(2px)" : "none",
          }}
        />
      )}
      {backgroundImage === null && backgroundUrl !== null && (
        <img
          src={backgroundUrl}
          alt=""
          className={cn(
            "absolute top-0 left-0 size-full  rounded-2xl object-cover",
            blurThumbnail === true && "blur-sm"
          )}
        />
      )}
      {/* Color layer */}
      <div
        className={cn(
          "card-color-layer absolute top-0 left-0 size-full  rounded-2xl bg-white",
          variant === "yellow" && "bg-secondary-soft-yellow-2",
          variant === "dark" && "bg-[#36444b]/80"
        )}
        style={colorLayerOverrides}
      />
      {/* Content container */}
      <div
        className={cn(
          "card-content-container [--card-primary-color:#212121] [--card-secondary-color:#ce0868] relative z-1 flex-1 p-5 flex flex-col justify-between gap-[30px] text-(--card-primary-color)",
          variant === "yellow" &&
            "[&_.specialty-badge]:bg-white [&_.specialty-badge]:text-[#f80]",
          variant === "dark" &&
            "text-white [--card-primary-color:#ffffff] [--card-secondary-color:#fae6f0]",
          "[&_.card-date]:font-dm-sans [&_.card-date]:text-[11px]"
        )}
        style={contentStyleOverrides}
      >
        {children}
      </div>
    </div>
  );
}

export function CardRow({
  $gap,
  children,
  style,
  className,
  ...props
}: {
  $gap?: number;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("w-full flex justify-between", className)}
      style={{ gap: `${$gap ?? 6}px`, ...style }}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardColumn({
  $gap,
  children,
  style,
  className,
  ...props
}: {
  $gap?: number;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col", className)}
      style={{ gap: `${$gap ?? 4}px`, ...style }}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardDate({ date }: { date: Date | string | null | undefined }) {
  if (date === null) return null;
  if (date === undefined) return null;
  const dateObject = typeof date === "string" ? new Date(date) : date;
  return <span className="card-date">{renderDate(dateObject)}</span>;
}

export function ContentLabel({ type }: { type: string }) {
  const { t } = useTranslation();
  const labelConfig: { [key: string]: { Icon: React.FC; text: string } } =
    useMemo(
      () => ({
        article: { Icon: ArticleLabelIcon, text: t("Article") },

        playlist: { Icon: PlaylistLabelIcon, text: t("Playlist") },
        video: { Icon: VideoLabelIcon, text: t("Video") },
        podcast: { Icon: PodcastLabelIcon, text: t("Podcast") },
        infographic: {
          Icon: InfographicLabelIcon,
          text: t("Infographic"),
        },
      }),
      [t]
    );

  const config = labelConfig[type];
  return (
    <div className="w-fit rounded-lg bg-secondary-soft-pink-1 flex p-[1px_8px] justify-center items-center gap-1 text-secondary-raspberry-1 font-dm-sans text-[11px] font-normal leading-[18px] tracking-[0.001px]">
      <config.Icon />
      {config.text}
    </div>
  );
}

export function JuisciGPTLabel() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        height: "18px",
        width: "max-content",
        display: "flex",
        padding: "1px 8px",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        borderRadius: "10px",
        backgroundColor: "var(--color-secondary-juiscigpt)",
        fontSize: "11px",
        fontWeight: "400",
        color: "var(--color-primary-black)",
      }}
    >
      {t("Powered by JuisciGPT")}
      <JuisciGPTLabelIcon />
    </div>
  );
}

export function RoomBadge({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg bg-[#fcfed3] flex p-[1px_8px] justify-center items-center gap-1 text-[#395200] font-dm-sans text-[11px] font-semibold leading-[18px] tracking-[-0.0002em] [&_svg]:size-[11px]  [&_svg_path]:fill-[#395200]">
      <NavRoomsBlackIcon />
      <ClampLines lines={1}>{children}</ClampLines>
    </div>
  );
}

// TODO: separate each buttons into individual action components
export function CardActions({
  isLiked,
  onLike,
  onOptions,
}: {
  isLiked: boolean;
  onLike: (e: MouseEvent) => void;
  onOptions: (e: MouseEvent) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 10 }}>
      <div
        className="size-8  rounded-full shrink-0 border border-secondary-bluegrey-3 bg-white flex justify-center items-center"
        onClick={onLike}
      >
        {isLiked ? <HeartRedIcon /> : <HeartBlackIcon />}
      </div>
      <div
        className="size-8  rounded-full shrink-0 border border-secondary-bluegrey-3 bg-white flex justify-center items-center"
        onClick={onOptions}
      >
        <DotsIcon />
      </div>
    </div>
  );
}

export function CardType({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={cn(
        "text-(--card-secondary-color) font-dm-sans text-xs font-medium uppercase",
        className
      )}
      style={style}
    >
      {children}
    </div>
  );
}

export function CardTitle({
  lines,
  children,
  className,
  style,
}: ComponentProps<typeof ClampLines> & {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <ClampLines
      lines={lines}
      className={cn(
        "font-dm-sans text-base font-semibold leading-[19px] tracking-[0.32px] m-0",
        className
      )}
      style={style}
    >
      {children}
    </ClampLines>
  );
}

export function CardJournal({
  journal,
  onClick,
}: {
  journal:
    | { name?: string; image?: { url?: string; path?: string } | null }
    | null
    | undefined;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const { t } = useTranslation();
  const WEBAPP_JOURNAL_LOGO = useFeatureIsOn("WEBAPP_JOURNAL_LOGO");

  // Extract image with required url/path for ResponsiveImage
  const validImage = useMemo(() => {
    const img = journal?.image;
    if (img === undefined || img === null) return null;
    const url = "url" in img ? img.url : undefined;
    const path = "path" in img ? img.path : undefined;
    if (url === undefined || path === undefined) return null;
    return { url, path };
  }, [journal?.image]);

  return (
    <div className="flex flex-1 flex-col gap-3" onClick={onClick}>
      <div className="flex gap-1.5 items-center">
        <JournalIcon />
        <span className="text-primary-black font-dm-sans text-xs">
          {t("Published in")}
        </span>
      </div>
      {validImage !== null && WEBAPP_JOURNAL_LOGO ? (
        <div className="relative inline-block max-w-[120px] [&_.responsive-img-fallback]:absolute [&_.responsive-img-fallback]:top-0 [&_.responsive-img-fallback]:left-0 [&_.responsive-img-fallback]:w-full">
          <ResponsiveImage
            image={validImage}
            sizes="120px"
            alt={journal?.name}
            style={{
              display: "block",
              objectFit: "contain",
              width: "100%",
              height: "40px",
              maxWidth: "100%",
            }}
          />
        </div>
      ) : (
        <span className="text-primary-black font-dm-sans font-medium text-[11px] leading-[120%] line-clamp-3">
          {journal?.name}
        </span>
      )}
    </div>
  );
}

export function CardAuthor({
  primaryAuthor,
  authors,
}: {
  primaryAuthor: string | null;
  authors: string[];
}) {
  const { t } = useTranslation();
  return (
    <div className="flex-1 flex flex-col gap-3">
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        <PencilIcon />
        <span className="text-primary-black font-dm-sans text-xs">
          {t("Authors")}
        </span>
      </div>
      <span
        style={{
          color: "var(--color-primary-black)",
          fontSize: "11px",
          fontWeight: "500",
          lineHeight: "120%",
        }}
      >
        {primaryAuthor} <br />
        {authors.length > 0
          ? t("and {{authorsLength}} other authors", {
              authorsLength: authors.length,
            })
          : ""}
      </span>
    </div>
  );
}

export function CondensedCardFooter({
  avatar,
  logo,
  name,
  metrics,
  className,
}: {
  avatar: ComponentProps<typeof ProfilePicture>["user"] | null;
  logo: string | null;
  name: string | null;
  metrics: React.ReactNode | null;
  className?: string;
}) {
  return (
    <div style={{ display: "flex", gap: "6px" }}>
      {avatar !== null ? (
        <ProfilePicture user={avatar} height={30} badge={null} />
      ) : logo !== null ? (
        <div className="min-w-[30px] w-[30px]! h-[30px] object-cover shrink-0 rounded-full overflow-hidden bg-white p-[3px] border border-secondary-bluegrey-3">
          <img
            className="size-full  object-contain bg-transparent"
            src={logo}
            alt={name ?? undefined}
          />
        </div>
      ) : (
        <JuisciMainAvatar />
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <span
          style={{
            color:
              className === "dark" ? "#FFFFFF" : "var(--color-primary-black)",
            fontSize: "11px",
            fontWeight: "500",
          }}
        >
          {name}
        </span>
        <span
          style={{
            color:
              className === "dark"
                ? "#D1D1D1"
                : "var(--color-secondary-bluegrey-5)",
            fontSize: "10px",
            fontWeight: "500",
          }}
        >
          {metrics}
        </span>
      </div>
    </div>
  );
}
